﻿import { DataGrid, Fluent, GridPersistanceFlags, PersistedGridSettings, faIcon } from "@serenity-is/corelib";
import { CardViewMixinTexts } from "../ServerTypes/Texts";

export type CardViewType = "card" | "grid";

export interface CardViewMixinOptions<TItem> {
    grid: DataGrid<TItem, any>;
    /* This can also return a React JSX VDOM but in that case ReactDOM 
    should be available in the global namespace */
    renderItem: (item: TItem, index: number) => HTMLElement | any;
    viewType?: CardViewType;
    cardClass?: string;
}

/**
 * A mixin that can be applied to a DataGrid for card view functionality
 */
export class CardViewMixin<TItem> {

    declare private dataGrid: DataGrid<TItem, any>;
    declare private cardContainer: Fluent;

    constructor(private options: CardViewMixinOptions<TItem>) {
        var dg = this.dataGrid = options.grid;

        const dataViewClick = (e: Event) => this.switchView((e.target as HTMLElement)?.closest?.('[data-view]')?.getAttribute('data-view') as CardViewType);

        var btnGroup = (
            <div class="btn-group view-switch" role="group" style="float: right">
                <a href="javascript:;" title={CardViewMixinTexts.ListView}
                    class="btn btn-primary active" data-view="list" onClick={dataViewClick}><i class={faIcon("list")}></i></a>
                <a href="javascript:;" title={CardViewMixinTexts.CardView}
                    class="btn btn-primary" data-view="card" onClick={dataViewClick}><i class={faIcon("grip-horizontal")}></i></a>
            </div>) as HTMLElement;

        dg.element.findFirst('.grid-title').prepend(btnGroup);
        this.cardContainer = Fluent(<div class="view-container card-container" style="display: none;"></div>)
            .insertAfter(dg.element.findFirst('.grid-container'));

        if (options.viewType)
            this.switchView(this.options.viewType, false);

        this.resizeCardView();

        Fluent.on(dg.domNode, 'layout', () => this.resizeCardView());

        (dg.view as any).onDataChanged.subscribe(() => {
            this.updateCardItems();
        });

        var oldCurrentSettings = (dg as any).getCurrentSettings;
        (dg as any).getCurrentSettings = function (flag: GridPersistanceFlags) {
            var settings = oldCurrentSettings.apply(dg, [flag]);
            settings['viewType'] = btnGroup.querySelector<HTMLElement>('[data-view].active')?.dataset.view;
            return settings;
        };

        var self = this;
        var oldRestoreSettingsFrom = (dg as any).restoreSettingsFrom;
        (dg as any).restoreSettingsFrom = function (settings: PersistedGridSettings, flags?: GridPersistanceFlags) {
            oldRestoreSettingsFrom.apply(dg, [settings, flags]);
            var viewType = (settings as any)?.viewType;
            if (viewType == "card" ||
                viewType == "list")
                self.switchView(viewType, false);
        };
    }

    public switchView(viewType: CardViewType, persist: boolean = true) {

        var button = this.dataGrid.element.findFirst('.btn[data-view="' + viewType + '"]');

        button.addClass('active').parent().children().forEach(x => x !== button.getNode() && x.classList.remove("active"));

        var card = viewType == 'card';
        this.dataGrid.element.findFirst('.card-container').toggle(card);
        this.dataGrid.element.findFirst('.grid-container').toggle(!card);

        if (card)
            this.updateCardItems();

        if (persist)
            (this.dataGrid as any).persistSettings();

        this.dataGrid.element.trigger("layout");
    }

    private updateCardItems() {
        const colClass = this.options.cardClass ?? "col-12 col-md-6 col-lg-6 col-xxl-4";

        this.cardContainer.empty().append(
            <div className="row card-items">
                {this.dataGrid.getItems().map((item, index) =>
                    <div className={colClass} key={index}>
                        <div className="card" ref={el => {
                            var node = this.options.renderItem(item, index);
                            if (node instanceof Node) {
                                el.appendChild(node);
                            }
                            else {
                                (window["ReactDOM"] ?? window["preact"])?.render?.(node as any, el);
                            }
                        }}></div>
                    </div>
                )}
            </div>);
    }

    private resizeCardView() {
        var gc = this.dataGrid.domNode.querySelector('.grid-container') as HTMLElement;
        if (!Fluent.isVisibleLike(gc))
            return;
        var height = gc.offsetHeight;
        if (height) {
            var cardContainer = this.dataGrid.domNode.querySelector('.card-container') as HTMLElement;
            cardContainer && (cardContainer.style.height = height + 'px');
        }
    }
}